import { Observable } from 'rxjs';
import { combineEpics } from 'redux-observable';
import smoothscroll from 'smoothscroll-polyfill';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { WINDOW_AVAILABLE } from '../redux/app';
import { FLYER_PAGE, CARD_PAGE } from '../../routes';

import {
  UPDATE_SCROLL_POSITION,
  SCROLL_TO_ELEMENT,
} from '../redux/scrollPosition';
import { hasDifferentRoute } from '../utils/urls/urlComparer';

import type { ActionObs, Store, Obs } from '../types';

const SCROLL_TOP_OFFSET = 68;
// Params that come from the left nav.  Should not move the user around the page on change.
const IGNORED_QUERY_PARAMS = [
  'slug',
  'sort',
  'colors',
  'shape',
  'photo_count',
  'coins',
  'designers',
  'tab',
];
let isWindowAvailable = false;

const scrollPolyfill = function(scrollTarget, scrollParams) {
  if (scrollTarget) {
    if (typeof scrollTarget.scroll === 'function') {
      scrollTarget.scroll(scrollParams);
    } else {
      scrollTarget.scrollTop = scrollParams.top;
    }
  }
};

const scrollToPosition = function(scrollLocation, behavior) {
  const scrollParams = {
    behavior,
    top: scrollLocation,
  };

  scrollPolyfill(document.body, scrollParams);
  scrollPolyfill(document.documentElement, scrollParams);
};

export function scrollPositionInitEpic(action$: ActionObs, store: Store): Obs {
  return action$.ofType(WINDOW_AVAILABLE).flatMap(() => {
    if ('scrollRestoration' in window.history) {
      try {
        // when google crawls window.history.scrollRestoration is only available as a getter
        // setting this property will crash the page for google
        // wraping in try/catch should fix it for google
        window.history.scrollRestoration = 'manual';
      } catch (e) {
        /* silence */
      }
    }
    smoothscroll.polyfill();
    isWindowAvailable = true;

    return Observable.empty();
  });
}

export function scrollPositionEpic(action$: ActionObs, store: Store): Obs {
  return action$
    .ofType(WINDOW_AVAILABLE, UPDATE_SCROLL_POSITION)
    .flatMap(action => {
      if (!isWindowAvailable && action.type === UPDATE_SCROLL_POSITION) {
        return Observable.empty();
      }

      const {
        env: { isMobile },
        modals: { cardFilterModalOpen, flyerFilterModalOpen },
        route,
        singleCard,
      } = store.getState();
      const {
        currentRoute,
        currentRoute: { pathType, query: curQueryObj },
        previousRoute,
      } = route;
      const hasPreviousRoute = !isEmpty(previousRoute);
      const prevQuery = get(previousRoute, 'query');

      // Scroll the window after React has had the chance to render based on the location change.
      let scrollLocation = 0;
      let scrollBehavior = 'smooth';
      let scrollTarget;

      switch (pathType) {
        case FLYER_PAGE:
          // Scroll to the category selection dropdown if we change or have a default category selected.
          if (!flyerFilterModalOpen && curQueryObj.category) {
            scrollTarget = document.getElementById('category-selection');
          }
          break;
        case CARD_PAGE:
          // Don't scroll if the URL changes are for filter changes.
          if (
            cardFilterModalOpen ||
            isMobile ||
            (hasPreviousRoute &&
              !hasDifferentRoute(
                currentRoute,
                previousRoute,
                IGNORED_QUERY_PARAMS,
              ))
          ) {
            return Observable.empty();
          }

          // If there is a selected card, scroll to it
          if (curQueryObj.card && curQueryObj.card.length > 0) {
            // The card should be present on the page before we scroll to the tray
            let targetGridCard = document.querySelector(
              `.card-${curQueryObj.card}`,
            );

            // Since we're not in the initial load, let's scroll to the tray.
            scrollTarget = document.getElementById('card-tray');
          }
          // We don't have a card in the current query...
          else {
            // If we just closed the tray, then scroll to the previously selected card.
            if (
              hasPreviousRoute &&
              prevQuery &&
              typeof prevQuery.card !== 'undefined'
            ) {
              scrollTarget = document.querySelector(`.card-${prevQuery.card}`);
            }
          }
          break;
      }

      if (scrollTarget) {
        // If we have a location to scroll to, scroll to it...
        scrollLocation = scrollTarget.offsetTop - SCROLL_TOP_OFFSET;
        scrollToPosition(scrollLocation, scrollBehavior);
      }

      return Observable.empty();
    });
}

export function scrollToElementEpic(action$: ActionObs): Obs {
  return action$
    .ofType(SCROLL_TO_ELEMENT)
    .flatMap(({ payload: { elementId, yOffset } }) => {
      if (!isWindowAvailable) {
        return Observable.empty();
      }

      const targetElement = document.getElementById(elementId);

      if (targetElement) {
        scrollToPosition(targetElement.offsetTop + yOffset, 'smooth');
      }

      return Observable.empty();
    });
}

export default combineEpics(
  scrollPositionInitEpic,
  scrollPositionEpic,
  scrollToElementEpic,
);
